/**
 * Sentry Utilities
 *
 * Helper functions for setting Sentry context throughout the application.
 * These utilities ensure consistent tagging and context for multi-tenant isolation.
 *
 * IMPORTANT: All functions are SSR-safe and only execute in the browser.
 * The @sentry/react-router package only exports these functions on the client.
 */

// Check if we're in the browser
const isBrowser = typeof window !== "undefined";

// Sentry severity levels (matching Sentry's type)
export type SeverityLevel = "fatal" | "error" | "warning" | "log" | "info" | "debug";

// Lazy-load Sentry only in the browser to avoid SSR import issues
// Using 'any' type to avoid TypeScript analyzing the Sentry package during build
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let SentryModule: any = null;

async function getSentry(): Promise<any> {
  if (!isBrowser) return null;
  if (!SentryModule) {
    try {
      // Dynamic import only runs in browser
      SentryModule = await import("@sentry/react-router");
    } catch (err) {
      console.warn("Failed to load Sentry:", err);
      return null;
    }
  }
  return SentryModule;
}

/**
 * Set user context for Sentry events
 * Call this when user authentication state changes
 */
export function setSentryUser(user: { id: string; email?: string; username?: string } | null) {
  if (!isBrowser) return;
  getSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.setUser(user);
  });
}

/**
 * Set workspace context for Sentry events
 * Call this when entering a workspace
 */
export function setSentryWorkspaceContext(workspaceId: string | null) {
  if (!isBrowser) return;
  getSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.setTag("workspace_id", workspaceId ?? undefined);
  });
}

/**
 * Set brand context for Sentry events
 * Call this when a brand is selected
 */
export function setSentryBrandContext(brandId: string | null) {
  if (!isBrowser) return;
  getSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.setTag("brand_id", brandId ?? undefined);
  });
}

/**
 * Set product context for Sentry events
 * Call this when a product is selected
 */
export function setSentryProductContext(productId: string | null) {
  if (!isBrowser) return;
  getSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.setTag("product_id", productId ?? undefined);
  });
}

/**
 * Set tenant context for Sentry events
 * Call this when tenant information is available
 */
export function setSentryTenantContext(tenantId: string | null) {
  if (!isBrowser) return;
  getSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.setTag("tenant_id", tenantId ?? undefined);
  });
}

/**
 * Set all context at once (useful for route changes)
 */
export function setSentryContext(context: {
  tenantId?: string | null;
  workspaceId?: string | null;
  brandId?: string | null;
  productId?: string | null;
}) {
  if (context.tenantId !== undefined) {
    setSentryTenantContext(context.tenantId);
  }
  if (context.workspaceId !== undefined) {
    setSentryWorkspaceContext(context.workspaceId);
  }
  if (context.brandId !== undefined) {
    setSentryBrandContext(context.brandId);
  }
  if (context.productId !== undefined) {
    setSentryProductContext(context.productId);
  }
}

/**
 * Add a custom breadcrumb for user actions
 */
export function addSentryBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: SeverityLevel = "info"
) {
  if (!isBrowser) return;
  getSentry().then((Sentry) => {
    if (!Sentry) return;
    Sentry.addBreadcrumb({
      category,
      message,
      level,
      data,
    });
  });
}

/**
 * Capture a custom error with additional context
 */
export function captureError(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: SeverityLevel;
  }
) {
  if (!isBrowser) return;
  getSentry().then((Sentry) => {
    if (!Sentry) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Sentry.withScope((scope: any) => {
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }
      if (context?.extra) {
        scope.setContext("additional_info", context.extra);
      }
      if (context?.level) {
        scope.setLevel(context.level);
      }
      Sentry.captureException(error);
    });
  });
}

/**
 * Start a custom span for performance monitoring
 * Returns a function to end the span
 *
 * @param name - The name of the span
 * @param op - The operation type (e.g., "http.request", "db.query")
 * @param data - Span attributes (must be primitive values only)
 */
export function startSpan(
  name: string,
  op: string,
  data?: Record<string, string | number | boolean>
): () => void {
  if (!isBrowser) return () => {};

  let spanRef: { end: () => void } | null = null;
  let endRequested = false;

  getSentry().then((Sentry) => {
    if (!Sentry) return;
    const span = Sentry.startInactiveSpan({
      name,
      op,
      attributes: data,
    });
    spanRef = span;
    // If end was called before span was ready, end it now
    if (endRequested) {
      span.end();
    }
  });

  return () => {
    if (spanRef) {
      spanRef.end();
    } else {
      endRequested = true;
    }
  };
}

/**
 * Wrapper for Sentry.startSpan that runs a callback within a span context.
 * This is the recommended way to instrument async operations.
 */
export async function withSentrySpan<T>(
  options: { name: string; op: string; attributes?: Record<string, string | number | boolean> },
  callback: () => T | Promise<T>
): Promise<T> {
  if (!isBrowser) {
    return callback();
  }

  const Sentry = await getSentry();
  if (!Sentry) {
    return callback();
  }

  return Sentry.startSpan(options, callback);
}

